/**
 * InputManager - Handles keyboard and mouse input
 */

import { Vector2 } from '../math/Vector2';

export class InputManager {
  private keysDown: Set<string> = new Set();
  private keysPressed: Set<string> = new Set();
  private keysReleased: Set<string> = new Set();

  // Store original key values for text input (preserves case)
  private textInputKeys: string[] = [];

  private mousePosition: Vector2 = new Vector2(0, 0);
  private mouseButtons: Set<number> = new Set();
  private mouseButtonsPressed: Set<number> = new Set();
  private mouseButtonsReleased: Set<number> = new Set();
  private mouseWheelDelta: number = 0;

  private canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Keyboard events
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.onKeyUp(e));

    // Mouse events
    this.canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
    this.canvas.addEventListener('mouseup', (e) => this.onMouseUp(e));
    this.canvas.addEventListener('wheel', (e) => this.onMouseWheel(e));
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private onKeyDown(e: KeyboardEvent): void {
    const key = e.key.toLowerCase();
    if (!this.keysDown.has(key)) {
      this.keysPressed.add(key);
    }
    this.keysDown.add(key);

    // Store original key value for text input (preserves case and special keys)
    this.textInputKeys.push(e.key);

    // Prevent default for game keys and text input keys
    if (['w', 'a', 's', 'd', ' ', 'e', 'q', 'tab', 'backspace', 'enter', 'escape'].includes(key)) {
      e.preventDefault();
    }
  }

  private onKeyUp(e: KeyboardEvent): void {
    const key = e.key.toLowerCase();
    this.keysDown.delete(key);
    this.keysReleased.add(key);
  }

  private onMouseMove(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.mousePosition = new Vector2(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
  }

  private onMouseDown(e: MouseEvent): void {
    if (!this.mouseButtons.has(e.button)) {
      this.mouseButtonsPressed.add(e.button);
    }
    this.mouseButtons.add(e.button);
  }

  private onMouseUp(e: MouseEvent): void {
    this.mouseButtons.delete(e.button);
    this.mouseButtonsReleased.add(e.button);
  }

  private onMouseWheel(e: WheelEvent): void {
    this.mouseWheelDelta = e.deltaY;
    e.preventDefault();
  }

  /** Call at end of each frame to reset per-frame state */
  update(): void {
    this.keysPressed.clear();
    this.keysReleased.clear();
    this.mouseButtonsPressed.clear();
    this.mouseButtonsReleased.clear();
    this.mouseWheelDelta = 0;
    this.textInputKeys = [];
  }

  // Keyboard queries
  isKeyDown(key: string): boolean {
    return this.keysDown.has(key.toLowerCase());
  }

  isKeyPressed(key: string): boolean {
    return this.keysPressed.has(key.toLowerCase());
  }

  isKeyReleased(key: string): boolean {
    return this.keysReleased.has(key.toLowerCase());
  }

  /** Get all keys pressed this frame */
  getPressedKeys(): string[] {
    return Array.from(this.keysPressed);
  }

  /** Get all text input keys this frame (preserves original case) */
  getTextInputKeys(): string[] {
    return [...this.textInputKeys];
  }

  // Mouse queries
  getMousePosition(): Vector2 {
    return this.mousePosition.clone();
  }

  isMouseButtonDown(button: number): boolean {
    return this.mouseButtons.has(button);
  }

  isMouseButtonPressed(button: number): boolean {
    return this.mouseButtonsPressed.has(button);
  }

  isMouseButtonReleased(button: number): boolean {
    return this.mouseButtonsReleased.has(button);
  }

  getMouseWheelDelta(): number {
    return this.mouseWheelDelta;
  }

  // Movement helpers
  getMovementVector(): Vector2 {
    let x = 0;
    let y = 0;

    if (this.isKeyDown('w') || this.isKeyDown('arrowup')) y -= 1;
    if (this.isKeyDown('s') || this.isKeyDown('arrowdown')) y += 1;
    if (this.isKeyDown('a') || this.isKeyDown('arrowleft')) x -= 1;
    if (this.isKeyDown('d') || this.isKeyDown('arrowright')) x += 1;

    const vec = new Vector2(x, y);
    return vec.magnitude() > 0 ? vec.normalize() : vec;
  }

  destroy(): void {
    // Event listeners are attached to window/canvas, they'll be cleaned up when page unloads
  }
}

// Mouse button constants
export const MOUSE_LEFT = 0;
export const MOUSE_MIDDLE = 1;
export const MOUSE_RIGHT = 2;
